import React from "react";
import { find } from "lodash";
import styled from "styled-components";
import { Interval } from "@linkfluence/models/date";
import { Locale } from "@linkfluence/models/locale";
import { getRangeFromDates } from "@tootools/date";
import { Fieldset, Label } from "@ui-kit/form";
import Icon from "@ui-kit/icon";
import Tooltip from "@ui-kit/tooltip";
import Dropdown from "@radarly/components/panels/card-edition/sidebar/dropdown";
import { useFeature } from "@radarly/core/features/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { Card } from "@radarly/models/cards";
import { setCardTimeUnit } from "@radarly/models/cards/editor";
import { TimeUnit } from "@radarly/models/cards/types/time-unit";
import {
  getClauseFromCard,
  getDateFromClause,
} from "@radarly/models/clause/getter";
import { DatavizTimeUnit } from "@radarly/models/dataviz";

const Title = styled.div`
  display: flex;
  justify-content: space-between;
`;

type TimeUnitOptionType = { value: Interval; label: string };

export interface SharedPanelsCardEditionSidebarTimeUnitProps {
  dataviz: DatavizTimeUnit;
  card: Card;
  setCard: (card: Card) => void;
}

export const getOptions = (
  defaultInterval: Interval,
  locale: Locale,
  isHourTimeframeUpdateEnabled?: boolean
) => {
  const options = [];
  options.push({
    value: "default",
    label: isHourTimeframeUpdateEnabled
      ? tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::TIME_UNIT::AUTOMATIC")
      : `${tr(
          locale,
          "CUSTOM_PAGES::EDITOR::DATAVIZ::TIME_UNIT::" + defaultInterval
        )} (${tr(
          locale,
          "CUSTOM_PAGES::EDITOR::DATAVIZ::TIME_UNIT::DEFAULT"
        )})`,
  });
  if (defaultInterval === Interval.Month) {
    options.push({
      value: Interval.Week,
      label: tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::TIME_UNIT::WEEK"),
    });
  } else {
    options.push(
      {
        value: Interval.Week,
        label: tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::TIME_UNIT::WEEK"),
      },
      {
        value: Interval.Month,
        label: tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::TIME_UNIT::MONTH"),
      }
    );
  }

  return options;
};
export default function SharedPanelsCardEditionSidebarTimeUnit({
  dataviz,
  card,
  setCard,
}: SharedPanelsCardEditionSidebarTimeUnitProps) {
  const isHourTimeframeUpdateEnabled = useFeature("hour_timeframe_update");

  const locale = useCurrentUserLocale();
  const clause = getClauseFromCard(card);
  const period = getDateFromClause(clause);
  const defaultInterval = getRangeFromDates(
    period.from,
    period.to,
    undefined,
    undefined,
    undefined,
    isHourTimeframeUpdateEnabled
  ).interval;
  const OPTIONS = getOptions(
    defaultInterval,
    locale,
    isHourTimeframeUpdateEnabled
  );

  const selectedOption =
    find(OPTIONS, { value: dataviz.timeUnit }) || OPTIONS[0];

  return isHourTimeframeUpdateEnabled ? (
    <Fieldset>
      <Title>
        <Label>
          {tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::TIME_UNIT::LABEL")}
        </Label>
        <Tooltip
          tiny
          tooltip={tr(
            locale,
            "CUSTOM_PAGES::EDITOR::DATAVIZ::TIME_UNIT::TOOLTIP"
          )}
        >
          <div>
            <Icon glyph="circle-info" size="s" />
          </div>
        </Tooltip>
      </Title>
      <Dropdown
        value={selectedOption as TimeUnitOptionType}
        options={OPTIONS}
        onChange={({ value }) =>
          setCard(setCardTimeUnit(card, value as TimeUnit))
        }
      />
    </Fieldset>
  ) : (
    <Dropdown
      label={tr(locale, "CUSTOM_PAGES::EDITOR::DATAVIZ::TIME_UNIT::LABEL")}
      value={selectedOption as TimeUnitOptionType}
      options={OPTIONS}
      onChange={({ value }) =>
        setCard(setCardTimeUnit(card, value as TimeUnit))
      }
    />
  );
}
